import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {IMetroCategory} from 'components/metro/types';
import {colors} from 'components/providers';
import {BadgePercent, ChevronLeft, ChevronRight, Grid, Heart, X} from 'lucide-react';
import {useRouter} from 'next/router';
import React, {FC, useEffect, useState} from 'react';
import NextImageUI from 'ui/NextImage';

import {useTranslation} from '../../../../hooks';

interface MultiLevelDrawerProps {
  items: IMetroCategory[] | undefined;
  isLoading?: boolean;
  title?: string;
  triggerLabel?: string;
}

interface NavigationLevel {
  items: IMetroCategory[] | undefined;
  title: string;
  parentId?: string;
  slug?: string;
}

const MultiLevelDrawer: FC<MultiLevelDrawerProps> = ({
  items,
  title = 'Categorii de produse',
  triggerLabel = 'Categorii de produse',
  isLoading,
}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {push} = useRouter();
  const {t} = useTranslation();

  const [navigationStack, setNavigationStack] = useState<NavigationLevel[]>([{items, title}]);

  const currentLevel = navigationStack[navigationStack.length - 1];
  const canGoBack = navigationStack.length > 1;

  const handleItemClick = (item: IMetroCategory) => {
    if (item.children && item.children.length > 0) {
      // Navigate to submenu
      setNavigationStack((prev) => [
        ...prev,
        {items: item.children, title: item.name, parentId: item.id?.toString(), slug: item.slug},
      ]);
    } else {
      // Execute item action and close drawer
      push(`/categories/${item.slug}`);
      handleClose();
    }
  };

  const handleSeeAllClick = () => {
    push(`/categories/${currentLevel.slug}`);
    handleClose();
  };

  const handleFavoritesClick = () => {
    push('/favorite-product');
    handleClose();
  };

  const handlePromoClick = () => {
    push('/market-category/promo');
    handleClose();
  };

  const handleGoBack = () => {
    if (canGoBack) {
      setNavigationStack((prev) => prev.slice(0, -1));
    }
  };

  const handleClose = () => {
    onClose();
    // Reset to root level when closing
    setTimeout(() => {
      setNavigationStack([{items, title}]);
    }, 300); // Wait for drawer close animation
  };

  useEffect(() => {
    if (!isLoading) setNavigationStack([{items, title}]);
  }, [isLoading, items, title]);

  const renderNavigationItem = (item: IMetroCategory) => (
    <Flex
      key={item.id}
      as="button"
      w="100%"
      p="12px 16px"
      alignItems="center"
      justifyContent="space-between"
      _hover={{bg: 'gray.50'}}
      _active={{bg: 'gray.100'}}
      onClick={() => handleItemClick(item)}
      cursor="pointer"
      borderRadius="8px"
      transition="all 0.2s"
    >
      <Flex alignItems="center" flex="1">
        {item.img_small_thumbnail && (
          <Box mr="12px" color="blue.600">
            {item.img_small_thumbnail && (
              <NextImageUI width={25} height={25} src={item.img_small_thumbnail} alt={`Category ${item.name}`} />
            )}
          </Box>
        )}
        <Text textAlign="left" fontWeight="500" fontSize="14px" noOfLines={1}>
          {item.name}
        </Text>
      </Flex>

      <Flex alignItems="center" gap="8px">
        {item.children && item.children.length > 0 && <ChevronRight size={16} color="#666" />}
      </Flex>
    </Flex>
  );

  const renderFavoritesItem = () => (
    <Flex
      as="button"
      w="100%"
      p="12px 16px"
      alignItems="center"
      justifyContent="space-between"
      _hover={{bg: 'gray.50'}}
      _active={{bg: 'gray.100'}}
      onClick={handleFavoritesClick}
      cursor="pointer"
      borderRadius="8px"
      transition="all 0.2s"
    >
      <Flex alignItems="center" flex="1">
        <Box mr="12px" color="blue.600">
          <Heart size={25} color={colors.pink} fill={colors.pink} />
        </Box>
        <Text textAlign="left" fontWeight="500" fontSize="14px" noOfLines={1}>
          {t('FAVORITES')}
        </Text>
      </Flex>
    </Flex>
  );

  const renderPromoItem = () => (
    <Flex
      as="button"
      w="100%"
      p="12px 16px"
      alignItems="center"
      justifyContent="space-between"
      _hover={{bg: 'gray.50'}}
      _active={{bg: 'gray.100'}}
      onClick={handlePromoClick}
      cursor="pointer"
      borderRadius="8px"
      transition="all 0.2s"
    >
      <Flex alignItems="center" flex="1">
        <Box mr="12px" color="blue.600">
          <BadgePercent size={25} color={colors.pink} />
        </Box>
        <Text textAlign="left" fontWeight="500" fontSize="14px" noOfLines={1}>
          {t('PROFITABLE_SHELF')}
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <>
      <Button
        onClick={onOpen}
        bg="#1e3a8a"
        color={colors.white}
        _hover={{bg: '#2c4db0'}}
        _active={{bg: '#1a307a'}}
        borderRadius="8px"
        p="12px 16px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        aria-label={triggerLabel}
      >
        <Flex alignItems="center" w="100%">
          <Grid size={20} />
        </Flex>
      </Button>

      <Drawer isOpen={isOpen} placement="left" onClose={handleClose} size="sm">
        <DrawerOverlay />
        <DrawerContent maxW="80%">
          <DrawerHeader
            borderBottomWidth="1px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px="16px"
            py="12px"
          >
            <Flex alignItems="center" flex="1">
              {canGoBack && (
                <IconButton
                  aria-label="Go back"
                  icon={<ChevronLeft size={20} />}
                  variant="ghost"
                  onClick={handleGoBack}
                  size="sm"
                  mr="8px"
                />
              )}
              <Flex alignItems="center">
                <Text fontWeight="600" fontSize="18px">
                  {currentLevel.title}
                </Text>
              </Flex>
            </Flex>

            <IconButton
              aria-label="Close menu"
              icon={<X size={20} />}
              variant="ghost"
              onClick={handleClose}
              size="sm"
            />
          </DrawerHeader>

          <DrawerBody p="0">
            {isLoading ? (
              <Flex justify="center" align="center" h="200px">
                <Text>Loading...</Text>
              </Flex>
            ) : (
              <VStack spacing="0" align="stretch" p="8px">
                {!canGoBack && (
                  <>
                    {renderFavoritesItem()}
                    <Divider my="4px" />
                    {renderPromoItem()}
                    <Divider my="4px" />
                  </>
                )}

                {canGoBack && (
                  <>
                    <Flex
                      as="button"
                      w="100%"
                      p="12px 16px"
                      alignItems="center"
                      justifyContent="space-between"
                      _hover={{bg: 'gray.50'}}
                      _active={{bg: 'gray.100'}}
                      onClick={() => handleSeeAllClick()}
                      cursor="pointer"
                      borderRadius="8px"
                      transition="all 0.2s"
                    >
                      <Flex alignItems="center" flex="1">
                        <Text textAlign="left" fontWeight="500" fontSize="14px" noOfLines={1}>
                          {t('VIEW_ALL')}
                        </Text>
                      </Flex>
                      <Flex alignItems="center" gap="8px">
                        {/*Add count in future version*/}
                      </Flex>
                    </Flex>

                    <Divider my="4px" />
                  </>
                )}
                {currentLevel?.items?.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {renderNavigationItem(item)}
                    {currentLevel.items && index < currentLevel.items.length - 1 && <Divider my="4px" />}
                  </React.Fragment>
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MultiLevelDrawer;
